import { useState, type ReactNode } from 'react'
import { Button, Group, Segmented, Stack, Text } from '@grovus/design-system'
import { LANG_OPTIONS, SHARE_COPY, type Dict, type Lang } from '../i18n'
import { fmt } from '../lib/format'
import type { Theme } from '../lib/preferences'
import { Wordmark } from '../components/Wordmark'
import { shareDate } from './date'

interface Props {
  t: Dict
  lang: Lang
  setLang: (lang: Lang) => void
  theme: Theme
  toggleTheme: () => void
  companyName: string
  expiresAt: string
  /** The shell's period control, as the member header renders it. */
  period: ReactNode
  /** Settles `false` when the leave was not confirmed: the button is offered again. */
  onLeave: () => Promise<boolean>
}

/**
 * The guest view's header row (GRO-83 design, "The guest view"): the company and how long
 * the view lasts, the period, language and theme, and "Leave guest view". No switcher,
 * account panel or user menu: a guest has no account and sees one company.
 */
export function GuestHeader({ t, lang, setLang, theme, toggleTheme, companyName, expiresAt, period, onLeave }: Props) {
  const copy = SHARE_COPY[lang]
  const [leaving, setLeaving] = useState(false)
  const [leaveFailed, setLeaveFailed] = useState(false)
  return (
    <>
      <Group align="flex-end" gap={10}>
        <Wordmark />
      </Group>
      <Stack gap={0} mr="auto" data-testid="guest-header">
        <Text fw={600} style={{ overflowWrap: 'anywhere' }}>{companyName}</Text>
        <Text fz="xs" className="grv-muted">{fmt(copy.guestLine, { date: shareDate(expiresAt, lang) })}</Text>
      </Stack>
      {period}
      <Segmented value={lang} onChange={(v) => setLang(v as Lang)} data={LANG_OPTIONS} />
      <Button variant="default" onClick={toggleTheme}>{theme === 'light' ? t.dark : t.light}</Button>
      <Button
        variant="default"
        disabled={leaving}
        aria-busy={leaving || undefined}
        onClick={() => {
          setLeaving(true)
          setLeaveFailed(false)
          void onLeave().then((left) => {
            if (left) return
            setLeaving(false)
            setLeaveFailed(true)
          })
        }}
      >
        {copy.leave}
      </Button>
      {leaveFailed && <Text fz="sm" role="alert">{copy.failed}</Text>}
    </>
  )
}
